"use client";

import { useSearchPageMapData } from "@/hooks/search";
import { useDataFetchOrchestrator } from "@/hooks/use-data-fetch-orchestrator";
import { useUrlFilters } from "@/hooks/use-url-filters";
import { useUserLocation } from "@/hooks/use-user-location";
import { mapBoundsConstants, mapDefaults } from "@/info/map-defaults";
import { Location, LocationType } from "@/types/filters";
import { Bounds } from "@/types/map";
import { cn } from "@/utils/cn";
import { transformFilters } from "@/utils/search/transform-filters";
import { useMap } from "@vis.gl/react-google-maps";
import { LocateFixed } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { SearchAreaButton } from "./search/button";
export const baseClasses = "cursor-pointer font-medium whitespace-nowrap rounded-full px-3 py-1 transition-all duration-150 text-xs flex-shrink-0 flex items-center gap-1 border-1 active:scale-95";
export const selectedClasses = "bg-black/90 border-black/90 text-white shadow-md";
export const unselectedClasses = "bg-black/10 border-black/0 text-black hover:bg-black/70 hover:text-white active:bg-black/90";
interface LocationOverlayProps {
  isMapDataFetching: boolean;
}
interface LocationWithCoordinates {
  id: string;
  name: string;
  type: LocationType;
  coordinates?: {
    latitude: number;
    longitude: number;
  };
}

/**
 * Extract lat/lng from a location's coordinates, or null if missing/invalid.
 */
function getLocationCoordinates(location: LocationWithCoordinates) {
  if (location.coordinates?.latitude !== undefined && location.coordinates?.longitude !== undefined && !isNaN(location.coordinates.latitude) && !isNaN(location.coordinates.longitude)) {
    return {
      lat: location.coordinates.latitude,
      lng: location.coordinates.longitude
    };
  }
  return null;
}

/**
 * Check whether two lat/lng points are "close enough" to be considered the same location.
 * Uses ~100m tolerance (0.001 degrees). Only called after map idle — never mid-animation.
 */
function coordinatesMatch(a: {
  lat: number;
  lng: number;
}, b: {
  lat: number;
  lng: number;
}, tolerance = mapBoundsConstants.BOUNDS_CHANGE_THRESHOLD_DEGREES): boolean {
  return Math.abs(a.lat - b.lat) < tolerance && Math.abs(a.lng - b.lng) < tolerance;
}
const defLocations: Location[] = [];
export const LocationOverlay = function LocationOverlay({
  isMapDataFetching
}: LocationOverlayProps) {
  const map = useMap();
  const {
    filters,
    setFilters
  } = useUrlFilters();
  const {
    userLocation,
    requestLocation
  } = useUserLocation();

  // ── Core UI state ──────────────────────────────────────────────────
  // Which chip (or "userLocation" / "custom") is currently active.
  // Initialised from URL so page reloads restore correctly.
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(filters.selectedLocationId || null);
  const [isUserLocationActive, setIsUserLocationActive] = useState(false);
  // Whether to show the "Search this area" button
  const [showSearchArea, setShowSearchArea] = useState(false);

  // ── Refs ────────────────────────────────────────────────────────────
  // Suppresses idle-based coordinate matching while the map is being
  // moved programmatically (chip click, locate-me).  Cleared inside the
  // one-shot idle handler — NO setTimeout.
  const isProgrammaticMove = useRef(false);
  const lastSearchedBounds = useRef<Bounds | null>(null);
  const hasSetInitialBounds = useRef(false);

  // ── Derived data ───────────────────────────────────────────────────
  const uniqueLocations = filters.locations || defLocations;
  const hasLocationFilters = uniqueLocations.length > 0;

  // Track previous locations to detect actual changes
  const prevLocationsRef = useRef<string>(JSON.stringify(uniqueLocations.map(l => l.id)));

  // ── Store / orchestrator ───────────────────────────────────────────
  const mode = useSearchPageMapData(state => state.mode);
  const setMode = useSearchPageMapData(state => state.setMode);
  const fetchMapData = useSearchPageMapData(state => state.fetchMapData);
  const fetchNearByProjects = useSearchPageMapData(state => state.fetchNearByProjects);
  const {
    orchestrateFetch
  } = useDataFetchOrchestrator({
    mode,
    fetchMapData,
    fetchNearByProjects
  });

  // ── Helper: have bounds changed significantly? ─────────────────────
  const haveBoundsChanged = useCallback((currentBounds: Bounds | null): boolean => {
    if (!currentBounds || !lastSearchedBounds.current) return false;
    const threshold = mapBoundsConstants.BOUNDS_CHANGE_THRESHOLD_DEGREES;
    const last = lastSearchedBounds.current;
    return Math.abs(currentBounds.north - last.north) > threshold || Math.abs(currentBounds.south - last.south) > threshold || Math.abs(currentBounds.east - last.east) > threshold || Math.abs(currentBounds.west - last.west) > threshold;
  }, []);

  // ── Coordinate matching on idle ────────────────────────────────────
  // This is the ONLY place where we decide chip selection after user drags.
  // It runs on map `idle` and compares map center against:
  //   1. User GPS location → highlight locate-me
  //   2. Any chip's coordinates → highlight that chip
  //   3. Neither + bounds changed → show "Search this area"
  const matchCenterToLocation = useCallback(() => {
    if (!map) return;
    const mapCenter = map.getCenter();
    if (!mapCenter) return;
    const center = {
      lat: mapCenter.lat(),
      lng: mapCenter.lng()
    };

    // 1. Check user GPS location
    if (userLocation && coordinatesMatch(center, userLocation)) {
      setSelectedLocationId("userLocation");
      setIsUserLocationActive(true);
      setShowSearchArea(false);
      return;
    }

    // 2. Check each chip's coordinates
    for (const location of uniqueLocations) {
      const coords = getLocationCoordinates(location);
      if (coords && coordinatesMatch(center, coords)) {
        setSelectedLocationId(location.id);
        setIsUserLocationActive(false);
        setShowSearchArea(false);
        return;
      }
    }

    // 3. No match — deselect all chips.
    //    Also show "Search this area" if bounds changed significantly.
    setSelectedLocationId(null);
    setIsUserLocationActive(false);
    const currentBounds = map.getBounds()?.toJSON() as Bounds | null;
    setShowSearchArea(haveBoundsChanged(currentBounds));
  }, [map, userLocation, uniqueLocations, haveBoundsChanged]);

  // ── Set initial lastSearchedBounds after first data fetch ───────────
  useEffect(() => {
    if (!map || hasSetInitialBounds.current || isMapDataFetching) return;
    const currentBounds = map.getBounds()?.toJSON() as Bounds;
    if (currentBounds) {
      lastSearchedBounds.current = currentBounds;
      hasSetInitialBounds.current = true;
    }
  }, [map, isMapDataFetching]);

  // ── Persistent map event listeners ─────────────────────────────────
  // idle → coordinate matching (only when user is dragging, not programmatic)
  // dragstart → mark as user interaction
  useEffect(() => {
    if (!map) return;
    const idleListener = map.addListener("idle", () => {
      // During programmatic moves (chip clicks, locate-me), the one-shot
      // idle handler in those flows does the work. We just reset the flag
      // here and skip matching — matching will run on the NEXT idle (if
      // the user drags the map).
      if (isProgrammaticMove.current) {
        isProgrammaticMove.current = false;
        return;
      }
      matchCenterToLocation();
    });
    const dragStartListener = map.addListener("dragstart", () => {
      // User is interacting manually
      isProgrammaticMove.current = false;
      setIsUserLocationActive(false);
    });
    return () => {
      if (idleListener) idleListener.remove();
      if (dragStartListener) dragStartListener.remove();
    };
  }, [map, matchCenterToLocation]);

  // ── Persist selection when locations list changes ───────────────────
  // When a new location is added (e.g. user adds "Whitefield" via filters),
  // auto-select it. When a location is removed, fall back gracefully.
  useEffect(() => {
    if (!uniqueLocations || uniqueLocations.length === 0) return;
    const currentLocationIds = uniqueLocations.map(l => l.id);
    const currentKey = JSON.stringify(currentLocationIds);
    if (prevLocationsRef.current === currentKey) return;

    // Parse old IDs to find what's new
    const oldIds: string[] = (() => {
      try {
        return JSON.parse(prevLocationsRef.current);
      } catch {
        return [];
      }
    })();
    prevLocationsRef.current = currentKey;

    // Detect newly added locations (IDs in new list but not in old list)
    const newlyAdded = currentLocationIds.filter(id => !oldIds.includes(id));

    // Auto-select ONLY if this is a user action (adding a filter), NOT the
    // initial data load.  On initial load oldIds is empty (locations went
    // from [] → populated), so we skip auto-select and trust the URL.
    if (newlyAdded.length > 0 && oldIds.length > 0) {
      // Auto-select the last newly added location (most recent addition)
      const newestId = newlyAdded[newlyAdded.length - 1];
      setSelectedLocationId(newestId);
      setShowSearchArea(false);
      setIsUserLocationActive(false);
      return;
    }

    // No new additions — check if current selection is still valid
    if (selectedLocationId === "custom" || selectedLocationId === "userLocation") return;
    if (selectedLocationId && uniqueLocations.some(loc => loc.id === selectedLocationId)) return;

    // Selected location was removed — default to first
    if (uniqueLocations.length > 0) {
      setSelectedLocationId(uniqueLocations[0].id);
    }
  }, [uniqueLocations, selectedLocationId]);

  // ── Chip click handler ─────────────────────────────────────────────
  const handleLocationClick = useCallback((location: LocationWithCoordinates) => {
    if (!map) return;

    // STEP 1: Immediate UI updates
    setSelectedLocationId(location.id);
    setIsUserLocationActive(false);
    setShowSearchArea(false);
    isProgrammaticMove.current = true;

    // STEP 2: Move map
    const coordinates = getLocationCoordinates(location);
    if (!coordinates) {
      isProgrammaticMove.current = false;
      return;
    }
    const targetZoom = location.type === "project" ? mapDefaults.projectZoom - 1 // 16
    : mapDefaults.zoom; // 14

    if (location.type === "project" && mode !== "project") setMode("project");else if (location.type !== "project" && mode === "project") setMode("default");
    map.setCenter(coordinates);
    map.setZoom(targetZoom);

    // STEP 3: One-shot idle listener — fetch data + reset flag
    let hasHandledIdle = false;
    const idleListener = map.addListener("idle", () => {
      if (hasHandledIdle) return;
      hasHandledIdle = true;
      if (idleListener) idleListener.remove();
      const currentBounds = map.getBounds()?.toJSON() as Bounds;
      const currentZoom = map.getZoom();
      const {
        filtersForGraphQL,
        newFilters
      } = transformFilters({
        filters: {
          ...filters,
          selectedLocationId: location.id
        },
        selectedLocationId: location.id,
        mapBounds: currentBounds,
        mapZoom: currentZoom,
        from: "nav-overlay"
      });
      setFilters(newFilters, {
        useReplaceState: true
      });
      lastSearchedBounds.current = currentBounds;
      orchestrateFetch({
        filters: newFilters,
        filtersForGraphQL,
        map
      });

      // Leave isProgrammaticMove = true so the persistent idle handler
      // resets it on the next idle and skips matching.
    });
  }, [map, filters, setFilters, orchestrateFetch, mode, setMode]);

  // ── Locate-me handler ──────────────────────────────────────────────
  const pendingCenterRef = useRef(false);
  const centerAndSearch = useCallback((location: {
    lat: number;
    lng: number;
  }) => {
    if (!map) return;
    isProgrammaticMove.current = true;
    setSelectedLocationId("userLocation");
    setShowSearchArea(false);
    map.panTo(location);
    map.setZoom(15);
    let hasHandledIdle = false;
    const idleListener = map.addListener("idle", () => {
      if (hasHandledIdle) return;
      hasHandledIdle = true;
      if (idleListener) idleListener.remove();
      const currentBounds = map.getBounds()?.toJSON() as Bounds;
      const currentZoom = map.getZoom();
      const {
        filtersForGraphQL,
        newFilters
      } = transformFilters({
        filters,
        selectedLocationId: "custom",
        mapBounds: currentBounds,
        mapZoom: currentZoom,
        from: "search-area"
      });
      setFilters(newFilters, {
        useReplaceState: true
      });
      lastSearchedBounds.current = currentBounds;
      setIsUserLocationActive(true);
      orchestrateFetch({
        filters: newFilters,
        filtersForGraphQL,
        map
      });

      // Leave isProgrammaticMove = true so the persistent idle handler
      // resets it on the next idle and skips matching.
    });
  }, [map, filters, setFilters, orchestrateFetch]);
  const centerOnLocation = useCallback(async () => {
    if (!map) return;
    if (userLocation) {
      centerAndSearch(userLocation);
      return;
    }
    pendingCenterRef.current = true;
    try {
      await requestLocation();
    } catch {
      pendingCenterRef.current = false;
    }
  }, [map, userLocation, requestLocation, centerAndSearch]);

  // Center map when userLocation becomes available after requestLocation
  useEffect(() => {
    if (pendingCenterRef.current && userLocation && map) {
      pendingCenterRef.current = false;
      centerAndSearch(userLocation);
    }
  }, [userLocation, map, centerAndSearch]);

  // ── "Search this area" click handler ───────────────────────────────
  const handleSearchAreaClick = useCallback(() => {
    setSelectedLocationId("custom");
    setShowSearchArea(false);
  }, []);

  // Watch for data fetch completion after "Search this area" click
  useEffect(() => {
    if (!map) return;
    if (!isMapDataFetching && selectedLocationId === "custom" && !showSearchArea) {
      const newBounds = map.getBounds()?.toJSON() as Bounds;
      if (newBounds) {
        lastSearchedBounds.current = newBounds;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMapDataFetching, selectedLocationId, showSearchArea]);

  // ── Render ──────────────────────────────────────────────────────────
  return <>
			<div className="absolute top-3 left-1/2 z-20 -translate-x-1/2 bg-white/40 backdrop-blur-md rounded-full shadow-lg p-1 max-w-[calc(100dvw-2rem)] overflow-hidden">
				<div className="flex gap-1.5 overflow-x-auto scrollbar-hide">
					{/* Current Location Button */}
					<button type="button" onClick={centerOnLocation} className={cn(baseClasses, isUserLocationActive ? selectedClasses : unselectedClasses, "px-2")} aria-label="Center on my location" title="Center on my location">
						<LocateFixed size={14} />
					</button>

					{showSearchArea && <SearchAreaButton isLoading={isMapDataFetching} setSelectedIndex={() => {
          /* no-op: kept for interface compat */
        }} onSearchAreaClick={handleSearchAreaClick} />}

					{uniqueLocations.map(location => {
          const isSelected = location.id === selectedLocationId && !showSearchArea;
          const hasCoordinates = !!location.coordinates;
          return <button type="button" key={`${location.id}-${location.name}`} onClick={() => handleLocationClick(location)} disabled={isMapDataFetching || !hasCoordinates} className={cn(baseClasses, isSelected ? selectedClasses : unselectedClasses, !hasCoordinates ? "opacity-50 cursor-not-allowed" : "")} aria-label={`Select ${location.name} location${!hasCoordinates ? " (no coordinates available)" : ""}`} title={!hasCoordinates ? "Location coordinates not available" : undefined}>
								{isMapDataFetching && isSelected ? <>
										<div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
										<span>Searching...</span>
									</> : <span>{location.name}</span>}
							</button>;
        })}
				</div>
			</div>
		</>;
};